import { useCallback, useEffect, useState } from "react";

import { useGetUserQuery } from "../hooks/useGetUserQuery";
import { Achievement, Group, User } from "../types";

import { userDataContext } from "./UserDataContext";

type UserDataContextProviderProps = {
  children?: React.ReactNode;
};

export const localStorageCurrentGroupKey = "selectedGroup";

export const UserDataContextProvider: React.FC<UserDataContextProviderProps> = ({
  children,
}) => {
  const {
    data: userData,
    refetch: userDataRefetch,
    status: userDataQueryStatus,
    isFetching: isUserDataFetching,
  } = useGetUserQuery();
  const [userDetails, setUserDetails] = useState<User | null>(null);
  const [groups, setGroups] = useState<Group[] | null>(null);
  const [currentGroup, setCurrentGroup] = useState<Group | null>(null);
  const [
    currentAchievements,
    setCurrentAchievements,
  ] = useState<Achievement | null>(null);

  const setCurrentGroupAndAchievements = useCallback(
    (
      groupsList: Group[],
      achievementsList: Achievement[],
      currentGroup: string | null
    ) => {
      const selectedGroup = groupsList.find(({ id }) => id === currentGroup);
      const currentAchievements = achievementsList.find(
        ({ groupId }) => groupId === selectedGroup?.id
      );
      if (selectedGroup && currentAchievements) {
        setCurrentGroup(selectedGroup);
        setCurrentAchievements(currentAchievements);
      } else {
        setCurrentGroup(groupsList[0]);
        setCurrentAchievements(achievementsList[0]);
      }
    },
    []
  );

  const handleGroupChange = useCallback(
    async (groupId: string | undefined) => {
      await userDataRefetch();
      if (groupId && userData) {
        localStorage.setItem(localStorageCurrentGroupKey, groupId);
        setCurrentGroupAndAchievements(
          userData.groups,
          userData.achievements,
          groupId
        );
      }
    },
    [userData, userDataRefetch, setCurrentGroupAndAchievements]
  );

  useEffect(() => {
    if (userData) {
      const { id, attributes, groups, achievements } = userData;

      setUserDetails({ id, ...attributes });
      setGroups(groups);

      const previouslyPickedGroup = localStorage.getItem(
        localStorageCurrentGroupKey
      );

      setCurrentGroupAndAchievements(
        groups,
        achievements,
        previouslyPickedGroup
      );
    }
  }, [userData, setCurrentGroupAndAchievements]);

  return (
    <userDataContext.Provider
      value={{
        isUserDataFetching,
        userDataQueryStatus,
        userDetails,
        currentGroup,
        currentAchievements,
        groups,
        changeGroup: handleGroupChange,
      }}
    >
      {children}
    </userDataContext.Provider>
  );
};
